import type { JsonObject } from "@/api/common";
import type {
  TrainingEvalBaselineKey,
  TrainingEvalBaselineView,
  TrainingEvalRunSummaryView,
  TrainingEvalRunView,
  TrainingEvalScenarioView,
  TrainingEvalSuccessTermsView,
} from "@/api/trainingEval";
import { formatBranchHash } from "@/components/table/branchHash";
import {
  BASELINE_SERIES_KEY,
  configLine,
  joinDistinct,
  buildCompareRecord,
  buildCompareRuns,
  type CompareRun,
  type EvalResultSeries,
} from "./compare/evalCompareModel";
import { buildMergedResult, type EvalMergeExclusion } from "./compare/evalCompareMergeModel";
import { boxStats } from "./evalDistributionModel";
import { isSeedKey, scenarioIndexByName } from "./evalRunScenarioModel";

export type BaselineKeyReason = "not_ready" | "mixed" | "dataset_missing" | "task_missing";
export type BaselineKeyResult =
  | { key: TrainingEvalBaselineKey; configMissing: boolean; reason: null }
  | { key: null; reason: BaselineKeyReason };

// 선택한 config 의 schedule 결과가 없거나 config 이름이 없을 때 대신 조회하는 config 다.
// Dataset·TASK 는 그대로다.
export const BASELINE_FALLBACK_CONFIG = "default";

// inline YAML 로 제출한 run 은 config 이름이 없다. 그 run 은 default config 의 schedule 결과와 비교한다.
function baselineConfigOf(run: TrainingEvalRunSummaryView): string {
  return run.config_name || BASELINE_FALLBACK_CONFIG;
}

// config 필드 부재는 MLP-829 배포 전 상태다. 그래서 null 과 달리 안내하지 않는다.
export function baselineKeyOf(summaries: readonly TrainingEvalRunSummaryView[]): BaselineKeyResult {
  // @compat MLP-829 until=be:4399f2f1 partial
  if (summaries.length === 0 || summaries.some((run) => run.config_name === undefined)) {
    return { key: null, reason: "not_ready" };
  }
  if (summaries.some((run) => !run.dataset_registry_ref?.uuid))
    return { key: null, reason: "dataset_missing" };
  if (summaries.some((run) => !run.task)) return { key: null, reason: "task_missing" };
  const first = summaries[0];
  const configName = baselineConfigOf(first);
  if (
    summaries.some(
      (run) =>
        baselineConfigOf(run) !== configName ||
        run.dataset_registry_ref?.uuid !== first.dataset_registry_ref?.uuid ||
        run.task !== first.task,
    )
  ) {
    return { key: null, reason: "mixed" };
  }
  // 위 검사는 전체 선택에 적용된다. 여기서는 같은 불변식으로 첫 행의 타입도 좁힌다.
  if (!first.dataset_registry_ref?.uuid || !first.task) return { key: null, reason: "not_ready" };
  return {
    key: { configName, datasetUuid: first.dataset_registry_ref.uuid, task: first.task },
    configMissing: summaries.some((run) => !run.config_name),
    reason: null,
  };
}

// baseline 은 시리즈이면서 열이다. Policy·Scenario 표는 run 하나당 열 하나를 쓴다. baseline 의
// Seed 별 run 은 Seed 를 빼면 같은 구성이다. 그래서 한 열로 합치고, 값이 다른 항목만 병합해 적는다.
// 두 모양을 따로 전달하면 소비자마다 같은 optional 을 다시 좁혀야 한다. 그래서 한 객체로 둔다.
export type BaselineSeries = CompareRun & {
  baseline: NonNullable<EvalResultSeries["baseline"]>;
};

export type BaselineResult = {
  series: BaselineSeries | null;
  exclusions: EvalMergeExclusion[];
  // series 와 따로 둔다. 대신 조회한 결과를 병합하지 못해도, 제외 경고가 그 config 의 run 임을 알린다.
  fallbackConfig: string | null;
};

// Seed 마다 다른 값(SIMULATION_SEED 등)은 나온 순서대로 이어 붙인다. 같은 값이면 그대로다.
function mergeEnvVars(maps: readonly Record<string, string>[]): Record<string, string> {
  const valuesByKey = new Map<string, string[]>();
  for (const map of maps) {
    for (const [key, value] of Object.entries(map)) {
      const values = valuesByKey.get(key) ?? [];
      if (!values.includes(value)) values.push(value);
      valuesByKey.set(key, values);
    }
  }
  return Object.fromEntries([...valuesByKey].map(([key, values]) => [key, values.join(", ")]));
}

// 실제로 실행한 Seed 는 scenario 가 아니라 run params 에 있다. scenario 를 그대로 합치면 Seed 가
// 하나로 보인다. 병합한 열은 실행한 Seed 를 전부 적는다.
function mergedSeeds(items: readonly CompareRun[]): string | null {
  return joinDistinct(items.map((item) => item.simulationSeed));
}

// 상세의 Parameters 표는 run params 의 seed 키를 먼저 읽는다. 대표 run 의 단일 Seed 가 남아
// 있으면 그 값이 병합 목록 대신 표시된다. 그래서 seed 키를 전부 지우고 병합한 값만 남긴다.
function withMergedSeed(params: JsonObject, seeds: string): JsonObject {
  const rest = Object.entries(params).filter(([key]) => !isSeedKey(key));
  return { ...Object.fromEntries(rest), SIMULATION_SEED: seeds };
}

function mergeBaselineScenario(
  items: readonly CompareRun[],
  seeds: string | null,
): TrainingEvalScenarioView | null {
  const scenarios = items
    .map((item) => item.run.scenario)
    .filter((scenario): scenario is TrainingEvalScenarioView => scenario !== null);
  if (scenarios.length === 0) return null;
  const tunable = mergeEnvVars(scenarios.map((scenario) => scenario.tunable_env_vars));
  return {
    ...scenarios[0],
    fixed_env_vars: mergeEnvVars(scenarios.map((scenario) => scenario.fixed_env_vars)),
    tunable_env_vars: seeds === null ? tunable : { ...tunable, SIMULATION_SEED: seeds },
  };
}

function medianDuration(items: readonly CompareRun[]): number | null {
  const values = items
    .map((item) => item.summary.durationSec)
    .filter((value): value is number => value !== null);
  return values.length === 0 ? null : boxStats(values).median;
}

export function buildBaselineSeries(
  response: TrainingEvalBaselineView,
  details: ReadonlyMap<string, TrainingEvalRunView>,
  terms: ReadonlyMap<string, TrainingEvalSuccessTermsView>,
  scenarios: readonly TrainingEvalScenarioView[],
  // 선택한 config 의 결과가 없어 대신 조회한 config 다. 대체가 아니면 null.
  fallbackConfig: string | null,
): BaselineResult {
  if (response.session === null || response.group === null || response.runs.length === 0)
    return { series: null, exclusions: [], fallbackConfig };
  const scenarioByName = scenarioIndexByName(scenarios);
  const records = response.runs.map((summary) => {
    const detail = details.get(summary.id) ?? null;
    const scenarioName = detail?.scenario ?? summary.scenario;
    return buildCompareRecord({
      summary,
      detail,
      terms: terms.get(summary.id) ?? null,
      metrics: null,
      scenario: scenarioName === null ? null : (scenarioByName.get(scenarioName) ?? null),
    });
  });
  const runs = buildCompareRuns(records, {});
  const merged = buildMergedResult(runs);
  if (merged.includedRunCount === 0)
    return { series: null, exclusions: merged.exclusions, fallbackConfig };
  const included = merged.includedItems;
  const seeds = mergedSeeds(included);
  const fallbackPrefix = fallbackConfig === null ? "" : `${configLine(fallbackConfig)} · `;
  const label = `Baseline ${fallbackPrefix}${formatBranchHash("main", response.session.git_short_hash)}`;
  const series: BaselineSeries = {
    ...merged.series,
    // Seed 를 뺀 구성은 같다. 그래서 첫 run 을 대표로 쓰고 scenario 와 Seed 만 병합한다.
    run: {
      ...included[0].run,
      scenario: mergeBaselineScenario(included, seeds),
      params:
        seeds === null ? included[0].run.params : withMergedSeed(included[0].run.params, seeds),
    },
    simulationSeed: null,
    // pooling 한 시리즈는 소요 시간이 없다. baseline 은 Seed 별 실행의 중앙값을 대신 표시한다.
    summary: { ...merged.series.summary, durationSec: medianDuration(included) },
    key: BASELINE_SERIES_KEY,
    label,
    shortLabel: label,
    color: "var(--app-chart-baseline)",
    sublabel: `Seed ${merged.uniqueSeedCount}개 병합 · Episode ${merged.episodeCount}개`,
    // baseline run 들의 config 다. run 요약에 값이 없으면 대신 조회한 config 를 쓴다.
    config: merged.series.config ?? fallbackConfig,
    baseline: {
      hash: response.session.git_short_hash,
      seedCount: merged.uniqueSeedCount,
      episodeCount: merged.episodeCount,
      sessionId: response.session.id,
      sessionName: response.session.name,
      fallbackConfig,
    },
  };
  return { exclusions: merged.exclusions, series, fallbackConfig };
}

export type BaselineNotice = { key: string; tone: "info" | "warning"; message: string };
export type EvalBaselineState = {
  status: "idle" | "loading" | "success" | "error";
  series: BaselineSeries | null;
  notices: BaselineNotice[];
};

export function baselineKeyNotice(reason: BaselineKeyReason): BaselineNotice[] {
  if (reason === "not_ready") return [];
  const messages = {
    mixed: "모든 실행과 Policy가 같은 config, Dataset, TASK여야 baseline을 보여줄 수 있습니다.",
    dataset_missing: "Training Dataset이 없는 실행이 있어 baseline을 보여줄 수 없습니다.",
    task_missing: "TASK가 없는 실행이 있어 baseline을 보여줄 수 없습니다.",
  };
  return [{ key: "baseline-key", tone: "info", message: messages[reason] }];
}

export function baselineNotices(
  response: TrainingEvalBaselineView,
  result: BaselineResult,
  configMissing = false,
): BaselineNotice[] {
  const notices: BaselineNotice[] = [];
  const metadata = result.series?.baseline;
  // 다른 config(scratch 학습일 수 있다)와 비교하는 것이다. 그래서 일상 안내와 구분해 warning 으로 띄운다.
  // 병합한 결과가 없으면 "사용합니다" 가 아래 빈 결과 안내와 어긋난다. 그때는 조회 사실만 알린다.
  if (result.fallbackConfig !== null) {
    const cause = configMissing
      ? "config 이름이 없는 실행이 있어"
      : "선택한 config의 schedule 결과가 없어";
    notices.push({
      key: "baseline-fallback",
      tone: "warning",
      message:
        metadata === undefined
          ? `${cause} 같은 Dataset·TASK의 ${result.fallbackConfig} config 결과를 조회했습니다. 아래 경고는 그 결과의 실행입니다.`
          : `${cause} 같은 Dataset·TASK의 ${result.fallbackConfig} config 결과를 사용합니다.`,
    });
  }
  // config 이름이 없어 default 로 조회했는데 비었으면 대체 안내가 없다. 그래서 빈 결과 안내가 그 조회를 알린다.
  notices.push(
    metadata === undefined
      ? {
          key: "baseline-empty",
          tone: "info",
          message:
            configMissing && result.fallbackConfig === null
              ? `config 이름이 없는 실행이 있어 같은 Dataset·TASK의 ${BASELINE_FALLBACK_CONFIG} config 결과를 조회했지만, 병합 가능한 schedule 결과가 없어 baseline을 보여줄 수 없습니다.`
              : "조건에 맞는 병합 가능한 schedule 결과가 없어 baseline을 보여줄 수 없습니다.",
        }
      : {
          key: "baseline-pooled",
          tone: "info",
          message: `Baseline은 Seed ${metadata.seedCount}개, Episode ${metadata.episodeCount}개를 병합한 결과로, Schedule 기능으로 진행한 learning-core main 의 결과입니다. Schedule 은 GPU 여유가 있을 때 돌기 때문에 가장 최신 main 이 아닐 수 있습니다.`,
        },
  );
  const exclusionLabels = {
    missing_seed: "Seed가 없습니다",
    duplicate_seed: "Seed가 중복됩니다",
    terms_unavailable: "success term을 불러오지 못했습니다",
  };
  for (const exclusion of result.exclusions) {
    notices.push({
      key: `baseline-excluded:${exclusion.runId}`,
      tone: "warning",
      message: `Baseline에서 실행 ${exclusion.runId}을 제외했습니다. ${exclusionLabels[exclusion.kind]} (${exclusion.kind}).`,
    });
  }
  if (response.truncated)
    notices.push({
      key: "baseline-truncated",
      tone: "warning",
      message:
        result.series === null
          ? "Baseline 조회 상한으로 일부 Seed만 조회했습니다."
          : "Baseline 조회 상한으로 일부 Seed만 병합했습니다.",
    });
  return notices;
}
